import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { COLORS } from "../../constants";
import {
  desenvolvedorService,
  type SaborAliasCodigoItem,
  type SaborExemploSemAliasItem,
  type SaborPendenteItem,
  type SaborSimulacaoItem,
} from "../../services/desenvolvedorService";

const qkMap = ["desenvolvedor", "mapeamento-sabores"] as const;
const qkPend = ["desenvolvedor", "mapeamento-pendentes"] as const;
const qkCard = ["desenvolvedor", "mapeamento-cardapios"] as const;
const qkCode = ["desenvolvedor", "mapeamento-aliases-codigo"] as const;

type SubTab = "pendentes" | "banco" | "simular";

function labelFonte(fonte: SaborSimulacaoItem["fonte"]): string {
  switch (fonte) {
    case "banco":
      return "Banco";
    case "codigo_alias":
      return "Alias código";
    case "match_automatico":
      return "Match automático";
    case "sem_match":
      return "Sem match";
  }
}

function labelOrigem(origem: SaborPendenteItem["origem"]): string {
  return origem === "foodfly" ? "Foodfly" : "Cardápio Web";
}

const MapeamentoSaboresDev: React.FC = () => {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState<SubTab>("pendentes");
  const [dias, setDias] = useState(7);
  const [nomeOrigem, setNomeOrigem] = useState("");
  const [cardapioId, setCardapioId] = useState<number | "">("");
  const [canalDoce, setCanalDoce] = useState(false);
  const [obs, setObs] = useState("");
  const [simularTexto, setSimularTexto] = useState("");
  const [simularResultado, setSimularResultado] = useState<
    SaborSimulacaoItem[] | null
  >(null);

  const mapeamentosQuery = useQuery({
    queryKey: qkMap,
    queryFn: () => desenvolvedorService.listarMapeamentosSabores(false),
    enabled: subTab === "banco" || subTab === "pendentes",
  });

  const pendentesQuery = useQuery({
    queryKey: [...qkPend, dias],
    queryFn: () => desenvolvedorService.listarSaboresPendentes(dias),
    enabled: subTab === "pendentes",
  });

  const cardapiosQuery = useQuery({
    queryKey: qkCard,
    queryFn: () => desenvolvedorService.listarCardapiosMapeamento(),
  });

  const aliasesQuery = useQuery({
    queryKey: qkCode,
    queryFn: () => desenvolvedorService.listarAliasesCodigoSabores(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qkMap });
    void queryClient.invalidateQueries({ queryKey: qkPend });
  };

  const criarMutation = useMutation({
    mutationFn: () =>
      desenvolvedorService.criarMapeamentoSabor({
        nomeOrigem: nomeOrigem.trim(),
        cardapioId: Number(cardapioId),
        canalDoce,
        observacao: obs.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Mapeamento criado — passa a valer nos próximos pedidos.");
      setNomeOrigem("");
      setCardapioId("");
      setCanalDoce(false);
      setObs("");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Falha ao criar."),
  });

  const toggleMutation = useMutation({
    mutationFn: (params: { id: number; ativo: boolean }) =>
      desenvolvedorService.atualizarMapeamentoSabor(params.id, {
        ativo: params.ativo,
      }),
    onSuccess: (item) => {
      toast.success(
        item.ativo
          ? "Mapeamento reativado."
          : "Mapeamento desativado (volta o fluxo automático/código).",
      );
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Falha ao atualizar."),
  });

  const simularMutation = useMutation({
    mutationFn: (nomes: string[]) =>
      desenvolvedorService.simularMapeamentoSabores(nomes),
    onSuccess: (itens) => setSimularResultado(itens),
    onError: (e: Error) => toast.error(e.message || "Falha ao simular."),
  });

  const cardapios = useMemo(
    () => cardapiosQuery.data ?? [],
    [cardapiosQuery.data],
  );
  const pendentes = useMemo(
    () => pendentesQuery.data ?? [],
    [pendentesQuery.data],
  );
  const mapeamentos = useMemo(
    () => mapeamentosQuery.data ?? [],
    [mapeamentosQuery.data],
  );
  const aliasesCodigo = useMemo(
    () => aliasesQuery.data?.itens ?? [],
    [aliasesQuery.data],
  );
  const exemplosSemAlias = useMemo(
    () => aliasesQuery.data?.exemplosSemAlias ?? [],
    [aliasesQuery.data],
  );

  const mapearPendente = (p: SaborPendenteItem) => {
    setSubTab("banco");
    setNomeOrigem(p.nomeOrigem);
    setCanalDoce(p.canalDoce);
  };

  const tabs: { key: SubTab; label: string }[] = [
    { key: "pendentes", label: "Pendentes" },
    { key: "banco", label: "Mapeados (banco)" },
    { key: "simular", label: "Simular" },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-950">
        <p className="font-semibold">Sem risco no que já funciona</p>
        <p className="mt-1">
          Com a tabela vazia, o match continua igual (aliases em código + fuzzy).
          Só regras <strong>ativas no banco</strong> passam na frente — e você
          pode desativá-las a qualquer momento.
        </p>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">
              Mapeamentos no código ({aliasesCodigo.length})
            </h2>
            <p className="mt-1 text-sm text-neutral-600">
              Aliases embutidos no deploy — somente leitura. A resolução abaixo
              ignora o banco para mostrar o que o código faz sozinho. Para
              sobrescrever, crie a mesma chave em &quot;Mapeados (banco)&quot;.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium"
            onClick={() => void aliasesQuery.refetch()}
          >
            Atualizar
          </button>
        </div>
        {aliasesQuery.isLoading ? (
          <p className="text-neutral-600">Carregando aliases do código…</p>
        ) : aliasesQuery.isError ? (
          <p className="text-red-600">
            Não foi possível carregar os mapeamentos do código.
          </p>
        ) : aliasesCodigo.length === 0 ? (
          <p className="text-neutral-600">Nenhum alias definido no código.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-neutral-200">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-600">
                <tr>
                  <th className="px-3 py-2">Chave</th>
                  <th className="px-3 py-2">Exemplo integração</th>
                  <th className="px-3 py-2">→ Nome canônico</th>
                  <th className="px-3 py-2">Cardápio Dom</th>
                  <th className="px-3 py-2">Fonte</th>
                  <th className="px-3 py-2">Arquivo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {aliasesCodigo.map((a: SaborAliasCodigoItem) => (
                  <tr key={`${a.canalDoce}-${a.chave}`}>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                      {a.canalDoce ? "doce:" : ""}
                      {a.chave}
                    </td>
                    <td className="px-3 py-2">{a.nomeExemplo}</td>
                    <td className="px-3 py-2 font-medium">{a.nomeCanonico}</td>
                    <td className="px-3 py-2">
                      {a.cardapioId != null ? (
                        <span>
                          #{a.cardapioId} {a.cardapioNome}
                        </span>
                      ) : (
                        <span className="font-medium text-red-600">
                          sem match
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {labelFonte(a.resolucaoFonte)}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">
                      {a.arquivo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
        <h2 className="text-lg font-bold text-amber-950">
          Sem alias no código — match automático
        </h2>
        <p className="mt-1 text-sm text-amber-900">
          Nomes que não têm regra fixa em{" "}
          <code className="text-xs">foodflySaborAliases.catalog.ts</code>. Quando
          há vários sabores parecidos no cardápio, a coluna &quot;Similares&quot;
          lista todos para conferência.
        </p>
        {aliasesQuery.isLoading ? (
          <p className="mt-3 text-amber-900">Carregando…</p>
        ) : exemplosSemAlias.length === 0 ? (
          <p className="mt-3 text-amber-900">Nenhum exemplo configurado.</p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-md border border-amber-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-amber-100/80 text-xs font-semibold uppercase text-amber-950">
                <tr>
                  <th className="px-3 py-2">Nome integração</th>
                  <th className="px-3 py-2">Chave</th>
                  <th className="px-3 py-2">Resolve para</th>
                  <th className="px-3 py-2">Fonte</th>
                  <th className="px-3 py-2">Similares no cardápio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {exemplosSemAlias.map((e: SaborExemploSemAliasItem) => (
                  <tr key={e.nomeOrigem}>
                    <td className="px-3 py-2 font-medium">{e.nomeOrigem}</td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                      {e.chave}
                    </td>
                    <td className="px-3 py-2">
                      {e.cardapioId != null ? (
                        <span>
                          #{e.cardapioId} {e.cardapioNome}
                        </span>
                      ) : (
                        <span className="font-medium text-red-600">
                          sem match
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {labelFonte(e.fonte)}
                    </td>
                    <td className="px-3 py-2">
                      {e.cardapiosSimilares.length > 1 ? (
                        <ul className="space-y-0.5 text-xs text-neutral-700">
                          {e.cardapiosSimilares.map((c) => (
                            <li
                              key={c.id}
                              className={
                                c.id === e.cardapioId
                                  ? "font-semibold text-emerald-800"
                                  : undefined
                              }
                            >
                              #{c.id} {c.nome}
                              {c.id === e.cardapioId ? " ← escolhido" : ""}
                            </li>
                          ))}
                        </ul>
                      ) : e.cardapiosSimilares.length === 1 ? (
                        <span className="text-xs text-neutral-500">
                          único candidato
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-2 border-b border-neutral-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSubTab(t.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
              subTab === t.key
                ? "text-white"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            style={
              subTab === t.key ? { backgroundColor: COLORS.PRIMARY } : undefined
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "pendentes" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="text-sm font-medium text-neutral-700">
              Últimos dias
              <select
                className="mt-1 block rounded-md border border-neutral-300 px-3 py-2 text-sm"
                value={dias}
                onChange={(e) => setDias(Number(e.target.value))}
              >
                {[3, 7, 14, 30].map((d) => (
                  <option key={d} value={d}>
                    {d} dias
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium"
              onClick={() => void pendentesQuery.refetch()}
            >
              Atualizar
            </button>
          </div>
          <p className="mb-3 text-sm text-neutral-600">
            Nomes vistos em pedidos recentes que ainda não resolvem para um
            item de <code>cardapios</code>.
          </p>
          {pendentesQuery.isLoading ? (
            <p className="text-neutral-600">Carregando…</p>
          ) : pendentesQuery.isError ? (
            <p className="text-red-600">Não foi possível carregar pendentes.</p>
          ) : pendentes.length === 0 ? (
            <p className="text-neutral-600">Nenhum pendente no período.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
              {pendentes.map((p) => (
                <li
                  key={`${p.origem}-${p.canalDoce}-${p.chave}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <div>
                    <span className="font-medium">{p.nomeOrigem}</span>
                    <span className="ml-2 text-neutral-500">
                      {labelOrigem(p.origem)} · {p.ocorrencias}×
                      {p.canalDoce ? " · doce" : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg px-3 py-1 text-sm font-semibold text-white"
                    style={{ backgroundColor: COLORS.PRIMARY }}
                    onClick={() => mapearPendente(p)}
                  >
                    Mapear
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {subTab === "banco" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-bold text-neutral-900">
            Novo mapeamento
          </h2>
          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-neutral-700 sm:col-span-2">
              Nome como chega na integração
              <input
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                value={nomeOrigem}
                onChange={(e) => setNomeOrigem(e.target.value)}
                placeholder='Ex.: Quatro Queijos ou (Doce) - Lacta'
              />
            </label>
            <label className="text-sm font-medium text-neutral-700 sm:col-span-2">
              Sabor no Dom (cardápio)
              <select
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                value={cardapioId}
                onChange={(e) => {
                  const v = e.target.value;
                  setCardapioId(v === "" ? "" : Number(v));
                }}
              >
                <option value="">Selecione…</option>
                {cardapios.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.id} — {c.nome} ({c.tipo})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={canalDoce}
                onChange={(e) => setCanalDoce(e.target.checked)}
              />
              Canal doce <code className="text-xs">(Doce) - …</code>
            </label>
            <label className="text-sm font-medium text-neutral-700 sm:col-span-2">
              Observação (opcional)
              <input
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                value={obs}
                onChange={(e) => setObs(e.target.value)}
              />
            </label>
            <div className="sm:col-span-2">
              <button
                type="button"
                disabled={
                  criarMutation.isPending ||
                  !nomeOrigem.trim() ||
                  cardapioId === ""
                }
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: COLORS.PRIMARY }}
                onClick={() => criarMutation.mutate()}
              >
                {criarMutation.isPending ? "Salvando…" : "Salvar mapeamento"}
              </button>
            </div>
          </div>

          <h2 className="mb-2 text-lg font-bold text-neutral-900">
            Regras no banco ({mapeamentos.length})
          </h2>
          {mapeamentosQuery.isLoading ? (
            <p className="text-neutral-600">Carregando…</p>
          ) : mapeamentos.length === 0 ? (
            <p className="text-neutral-600">
              Nenhum mapeamento no banco — fluxo atual intacto.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
              {mapeamentos.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <div>
                    <span className="font-medium">{m.nomeOrigem}</span>
                    <span className="mx-1 text-neutral-400">→</span>
                    <span>
                      #{m.cardapioId} {m.cardapioNome}
                    </span>
                    {!m.ativo ? (
                      <span className="ml-2 rounded bg-neutral-200 px-1.5 py-0.5 text-[11px] font-bold uppercase text-neutral-700">
                        Inativo
                      </span>
                    ) : null}
                    {m.canalDoce ? (
                      <span className="ml-2 text-neutral-500">doce</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-neutral-300 px-3 py-1 text-sm font-medium"
                    disabled={toggleMutation.isPending}
                    onClick={() =>
                      toggleMutation.mutate({ id: m.id, ativo: !m.ativo })
                    }
                  >
                    {m.ativo ? "Desativar" : "Reativar"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {subTab === "simular" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <p className="mb-3 text-sm text-neutral-600">
            Um nome por linha. Mostra se resolve via banco, alias de código,
            match automático ou falha.
          </p>
          <textarea
            className="mb-3 h-36 w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm"
            value={simularTexto}
            onChange={(e) => setSimularTexto(e.target.value)}
            placeholder={"Quatro Queijos\n(Doce) - Lacta\nBatata Frita"}
          />
          <button
            type="button"
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: COLORS.PRIMARY }}
            disabled={simularMutation.isPending || !simularTexto.trim()}
            onClick={() => {
              const nomes = simularTexto
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean);
              simularMutation.mutate(nomes);
            }}
          >
            {simularMutation.isPending ? "Simulando…" : "Simular"}
          </button>
          {simularResultado ? (
            <ul className="mt-4 divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
              {simularResultado.map((r) => (
                <li key={`${r.chave}-${r.nomeOrigem}`} className="px-3 py-2">
                  <span className="font-medium">{r.nomeOrigem}</span>
                  <span className="mx-2 text-neutral-400">→</span>
                  {r.cardapioId != null ? (
                    <span>
                      #{r.cardapioId} {r.cardapioNome}
                    </span>
                  ) : (
                    <span className="text-red-600">sem match</span>
                  )}
                  <span className="ml-2 text-neutral-500">
                    ({labelFonte(r.fonte)})
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default MapeamentoSaboresDev;
